import { type PassageSource, passageSource } from '@/features/humint/recording/recording-path'

export interface AudioEvidence {
  kind: 'recording' | 'voice'
  voice: number | null
  speechSeconds: number | null
  minimumSpeechSeconds: number | null
  matchingStatus: 'pending' | 'insufficient_speech' | 'ready' | null
  shortEvidence: boolean
  /** The recording's document, as the node names it; what a voice's clip is derived from. */
  documentId: string | null
  source: PassageSource | null
}

function nonnegativeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0 ? value : null
}

function nonemptyText(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null
}

/** Visible recording/speaker evidence opens the same source as its transcript passages. */
export function audioEvidence(
  caseId: string,
  labels: readonly string[],
  properties: Record<string, unknown>
): AudioEvidence | null {
  const voice = labels.includes('Voice')
  const recording =
    labels.includes('Recording') ||
    (labels.includes('AttachedDocument') && properties.file_type === 'audio')
  if (!voice && !recording) return null
  const status = properties.matching_status
  const voiceNumber = nonnegativeNumber(properties.voice)
  const documentId = nonemptyText(properties.document_id)
  return {
    kind: voice ? 'voice' : 'recording',
    voice:
      voiceNumber !== null && Number.isInteger(voiceNumber) && voiceNumber > 0 ? voiceNumber : null,
    speechSeconds: nonnegativeNumber(properties.speech_seconds),
    minimumSpeechSeconds: nonnegativeNumber(properties.minimum_speech_seconds),
    matchingStatus:
      status === 'pending' || status === 'insufficient_speech' || status === 'ready'
        ? status
        : null,
    shortEvidence: properties.short_evidence === true,
    documentId,
    source: passageSource(caseId, { captureId: nonemptyText(properties.capture_id), documentId }),
  }
}
